import React, { useRef } from 'react';
import { Button, Col, Input, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';

import { crmInformation, manualCall } from '../../../store/slices/callingSlice';
import { setSidebarType } from '../../../store/slices/commonSlice';
import {
  setCallDialSec,
  setCallId,
  setCurrentCallDetail,
  setScreenType,
  setSelectBreakType
} from '../../../store/slices/dialSlice';
import { useUserDetails } from '../../../store/slices/authSlice';
import { useCallingData } from '../../Context/CallingContext';

const DialPadScreen = ({ duration, number, setNumber, onIdleSet }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const user = useUserDetails();
  const { handleTimeReset } = useCallingData();

  // Default timeout duration, in seconds
  const timeoutDuration = Number(user?.process_details?.autoOutbound) || 5;

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      dispatch(setSelectBreakType(null));
      onIdleSet();
      handleTimeReset();
      setNumber('');
    }, timeoutDuration * 1000);
  };

  const onNumberChange = (val) => {
    resetTimer();
    if (number?.length < val.length && number?.length === 0) {
      dispatch(setSelectBreakType({ name: 'OUTGOING', color: 'warning' }));
      handleTimeReset();
    } else if (number?.length > val.length && val.length === 0) {
      clearTimeout(timeoutRef.current);
      dispatch(setSelectBreakType(null));
      onIdleSet();
      handleTimeReset();
    }

    if (number?.length < 20) {
      setNumber(number + val);
    }
  };

  // Fetch CRM details for the current call
  const getCrmDetails = async (data) => {
    try {
      const payload = {
        crm_id: data?.crm_id,
        unique_id: data?.unique_id
      };
      const response = await dispatch(crmInformation(payload)).unwrap();
      if (response) {
        dispatch(setSidebarType('CRM'));
        navigate('/crm');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const dialNumber = async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    try {
      const payload = {
        destination_number: number
      };
      const response = await dispatch(manualCall(payload)).unwrap();
      if (response && response?.data) {
        dispatch(setCallDialSec(duration));
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setCurrentCallDetail(response?.data));
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleRemoveIconClick = () => {
    const num = number.slice(0, -1) || '';
    resetTimer();

    if (number?.length > 0 && num.length === 0) {
      clearTimeout(timeoutRef.current);
      dispatch(setSelectBreakType(null));
      onIdleSet();
      handleTimeReset();
    }
    setNumber(num);
  };

  return (
    <div className="input-container">
      <div className="input-wrapper">
        <Input
          type="text"
          value={number}
          onChange={(e) => {
            const value = e.target.value;
            const val = value.replace(/[^0-9*#]+/g, '');
            resetTimer();
            if (number?.length < val.length && number?.length === 0) {
              dispatch(setSelectBreakType({ name: 'OUTGOING', color: 'warning' }));
              handleTimeReset();
            } else if (number?.length > val.length && val.length === 0) {
              clearTimeout(timeoutRef.current);
              dispatch(setSelectBreakType(null));
              onIdleSet();
              handleTimeReset();
            }
            if (val?.length < 20) setNumber(val);
          }}
          className="dail-input"
          pattern="[0-9]+"
        />
        <div className="remove-icon-wrapper" onClick={handleRemoveIconClick}>
          <Icon.Delete className="remove-icon" />
        </div>
      </div>

      <Row className="button-grid pb-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((val, idx) => (
          <Col key={idx} className="button-col p-0">
            <div className="dial-button" onClick={() => onNumberChange(val)}>
              <span>{val}</span>
            </div>
          </Col>
        ))}
        <div></div>
        <div className="call-button-wrapper">
          <Button className="call-button" onClick={() => dialNumber()}>
            <Icon.PhoneCall className="call-icon" />
          </Button>
        </div>
      </Row>
    </div>
  );
};

DialPadScreen.propTypes = {
  number: PropTypes.string,
  setNumber: PropTypes.func,
  onIdleSet: PropTypes.func,
  duration: PropTypes.any
};

export default DialPadScreen;
